import { useCallback, useLayoutEffect, useRef } from 'react';
import { gsap } from 'gsap';

/**
 * Signature view transition — opacity + blur + translateY, staggered.
 *
 * Replicates the FABEL NEXUS "liquid" reveal so every view/sub-view switch
 * blurs the current blocks out, swaps the state, then blurs the new blocks in.
 *
 *   IN-FX  : fromTo(targets, {opacity:0, y:12, filter:'blur(8px)'},
 *                       {opacity:1, y:0, filter:'blur(0px)', duration:0.55,
 *                        stagger:0.05, ease:'power3.out', delay:0.04})
 *   OUT-FX : to(targets, {opacity:0, y:-10, filter:'blur(12px)', duration:0.26,
 *                       stagger:0.018, ease:'power2.in', onComplete: swap})
 *
 * Usage: attach the returned `stageRef` to a wrapper keyed on `viewStageKey`
 * (so React remounts it on every view change), and route every view-switch
 * button through `requestViewChange(swap)`.
 */
export interface UseViewTransitionOptions {
  /** Selectors used to find the blocks to reveal. Falls back to the container. */
  blockSelectors?: string[];
  /** Bump to re-fire the reveal without changing the key (optional). */
  enabled?: boolean;
}

// Block selectors adapted to the UniBox class names. Kept broad so every view
// (sidebar, list rows, calendar, kanban, preview) participates in the reveal.
const DEFAULT_BLOCK_SELECTORS = [
  '[data-view-block]',
  '.unibox-sidebar',
  '.unibox-filterbar',
  '.unibox-list',
  '.unibox-list-row',
  '.unibox-calendar',
  '.unibox-kanban',
  '.unibox-preview',
];

export const collectViewBlocks = (container: HTMLElement | null, selectors: string[] = DEFAULT_BLOCK_SELECTORS): HTMLElement[] => {
  if (!container) return [];
  const blocks = container.querySelectorAll<HTMLElement>(selectors.join(','));
  return blocks.length ? Array.from(blocks) : [container];
};

export const useViewTransition = ({ blockSelectors = DEFAULT_BLOCK_SELECTORS, enabled = true }: UseViewTransitionOptions = {}) => {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const mountedRef = useRef(false);

  const revealIn = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const targets = collectViewBlocks(stage, blockSelectors);
    if (!targets.length) return;

    // gsap.context scopes selectors and lets us revert inline styles on cleanup.
    const ctx = gsap.context(() => {
      gsap.fromTo(
        targets,
        { opacity: 0, y: 12, filter: 'blur(8px)' },
        { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.55, stagger: 0.05, ease: 'power3.out', delay: 0.04 },
      );
    }, stage);

    return ctx;
  }, [blockSelectors]);

  // OUT-FX: blur the current blocks out, then run the state swap on complete.
  // If there is nothing to blur, swap synchronously.
  const transitionOutThen = useCallback(
    (swap: () => void, opts?: { y?: number; blur?: number; duration?: number; stagger?: number }) => {
      const stage = stageRef.current;
      const targets = collectViewBlocks(stage, blockSelectors);
      if (!targets.length) {
        swap();
        return;
      }
      gsap.to(targets, {
        opacity: 0,
        y: opts?.y ?? -10,
        filter: `blur(${opts?.blur ?? 12}px)`,
        duration: opts?.duration ?? 0.26,
        stagger: opts?.stagger ?? 0.018,
        ease: 'power2.in',
        onComplete: swap,
      });
    },
    [blockSelectors],
  );

  return { stageRef, revealIn, transitionOutThen, mountedRef, enabled };
};

/**
 * Wires the IN-FX to a stage that remounts on `viewStageKey` change.
 * Returns the ref to spread on the keyed wrapper. By default the first mount
 * is skipped (lets the initial paint appear cleanly); every subsequent
 * remount reveals its blocks synchronously in a layout effect (before paint,
 * so there is no flash — UniBox has no lazy/Suspense children, so no RAF
 * polling is needed).
 *
 * Pass `revealOnFirstMount: true` to fire the blur-in on the initial mount
 * too — use this for route-level views where the reveal IS the loading FX
 * (campaigns list, leads list, ISR workspace).
 */
export interface UseStageRevealOptions {
  /** Selectors used to find the blocks to reveal. Defaults to UniBox's. */
  blockSelectors?: string[];
  /** Master toggle. When false the hook is a no-op. */
  enabled?: boolean;
  /**
   * Fire the IN-FX on the very first mount. Default false (UniBox keeps its
   * clean initial paint). Route-level views that want the reveal as the
   * loading FX set this true.
   */
  revealOnFirstMount?: boolean;
}

export const useStageReveal = (
  viewStageKey: string,
  optionsOrSelectors?: string[] | UseStageRevealOptions,
  enabledArg?: boolean,
) => {
  // Backward-compat: accept the old positional signature
  // (viewStageKey, blockSelectors?, enabled?) OR the new options object.
  const opts: UseStageRevealOptions = Array.isArray(optionsOrSelectors)
    ? { blockSelectors: optionsOrSelectors, enabled: enabledArg ?? true }
    : (optionsOrSelectors ?? {});
  const blockSelectors = opts.blockSelectors ?? DEFAULT_BLOCK_SELECTORS;
  const enabled = opts.enabled ?? true;
  const revealOnFirstMount = opts.revealOnFirstMount ?? false;

  const stageRef = useRef<HTMLDivElement | null>(null);
  const mountedRef = useRef(false);

  useLayoutEffect(() => {
    const stage = stageRef.current;
    if (!stage || !enabled) return undefined;

    // Skip the very first mount unless the caller opted into revealing on
    // first mount (route-level views that use the reveal as the loading FX).
    if (!mountedRef.current) {
      mountedRef.current = true;
      if (!revealOnFirstMount) return undefined;
    }

    const targets = collectViewBlocks(stage, blockSelectors);
    if (!targets.length) return undefined;

    // gsap.fromTo sets the "from" state synchronously before the browser
    // paints, so the new view never flashes at full opacity.
    const ctx = gsap.context(() => {
      gsap.fromTo(
        targets,
        { opacity: 0, y: 12, filter: 'blur(8px)' },
        { opacity: 1, y: 0, filter: 'blur(0px)', duration: 0.55, stagger: 0.05, ease: 'power3.out', delay: 0.04 },
      );
    }, stage);

    return () => {
      ctx.revert();
    };
  }, [blockSelectors, enabled, revealOnFirstMount, viewStageKey]);

  return stageRef;
};
